// src/pages/gt3x/Rennabmeldung.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";

const SERIES_KEY = "gt3x";
const SEASON_KEY = "";

const API_BASE = String(
    process.env.REACT_APP_RV_API_BASE || "https://api.racevision.info"
).replace(/\/$/, "");

const API_ROOT = `${API_BASE}/api/race-absences`;

const AUTH_LOGIN_URL = `${API_BASE}/api/auth/discord/login`;

function readPossibleTokenFromValue(value) {
    if (!value || typeof value !== "string") return "";

    const trimmed = value.trim();

    if (trimmed.startsWith("eyJ")) {
        return trimmed;
    }

    try {
        const parsed = JSON.parse(trimmed);

        const candidates = [
            parsed?.token,
            parsed?.access_token,
            parsed?.accessToken,
            parsed?.jwt,
            parsed?.authToken,
            parsed?.user?.token,
            parsed?.user?.access_token,
        ];

        const found = candidates.find(
            (candidate) => typeof candidate === "string" && candidate.startsWith("eyJ")
        );

        return found || "";
    } catch {
        return "";
    }
}

function getAuthToken() {
    const directKeys = [
        "rv_token",
        "rv_auth_token",
        "racevision_token",
        "token",
        "access_token",
        "authToken",
        "jwt",
        "rv_auth",
        "auth",
        "user",
    ];

    for (const key of directKeys) {
        const localValue = window.localStorage.getItem(key);
        const sessionValue = window.sessionStorage.getItem(key);

        const localToken = readPossibleTokenFromValue(localValue);
        if (localToken) return localToken;

        const sessionToken = readPossibleTokenFromValue(sessionValue);
        if (sessionToken) return sessionToken;
    }

    const storages = [window.localStorage, window.sessionStorage];

    for (const storage of storages) {
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (!key) continue;

            const value = storage.getItem(key);
            const token = readPossibleTokenFromValue(value);

            if (token) return token;
        }
    }

    return "";
}

async function apiFetch(path, options = {}) {
    const token = getAuthToken();

    if (!token) {
        const err = new Error("LOGIN_REQUIRED");
        err.status = 401;
        throw err;
    }

    const url = `${API_ROOT}${path}`;

    const res = await fetch(url, {
        ...options,
        headers: {
            Authorization: `Bearer ${token}`,
            ...(options.body ? { "Content-Type": "application/json" } : {}),
            ...(options.headers || {}),
        },
    });

    const contentType = res.headers.get("content-type") || "";
    const rawText = await res.text();

    let data = null;

    if (contentType.includes("application/json")) {
        try {
            data = rawText ? JSON.parse(rawText) : null;
        } catch {
            throw new Error(`API hat ungültiges JSON geliefert: ${url}`);
        }
    } else {
        console.error("API lieferte kein JSON:", {
            url,
            status: res.status,
            contentType,
            preview: rawText.slice(0, 300),
        });

        throw new Error(
            `API liefert kein JSON. Prüfe REACT_APP_RV_API_BASE. Aufgerufen wurde: ${url}`
        );
    }

    if (!res.ok) {
        const err = new Error(
            data?.detail ||
            data?.error ||
            `API-Fehler ${res.status}`
        );
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

function formatDateTime(value) {
    if (!value) return "–";

    try {
        return new Date(value).toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(value);
    }
}

function StatusMessage({ type, children }) {
    if (!children) return null;

    const isError = type === "error";

    return (
        <div
            style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 8,
                border: isError
                    ? "1px solid rgba(255,44,70,0.6)"
                    : "1px solid rgba(105,251,144,0.6)",
                background: isError
                    ? "rgba(255,44,70,0.08)"
                    : "rgba(105,251,144,0.08)",
                color: isError ? "#ff8c8c" : "#69fb90",
                fontSize: "0.96rem",
            }}
        >
            {children}
        </div>
    );
}

function formatDriverLabel(driver) {
    if (!driver) return "";

    const carNumber = driver.car_number ? `#${driver.car_number}` : "";
    const name = driver.driver_name || driver.name || "";

    return [carNumber, name].filter(Boolean).join(" ") || "Fahrer";
}

function RaceSelect({ label, value, onChange, races, disabled }) {
    return (
        <label
            className="rv-dd"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
            }}
        >
            <span className="rv-dd__label">{label}</span>

            <CustomSelect
                value={value ?? ""}
                onChange={(nextValue) => onChange?.(Number(nextValue))}
                disabled={disabled}
                placeholder={disabled ? "Lade Rennen…" : "Rennen auswählen…"}
                buttonClassName="rv-select--race"
                options={races.map((race) => ({
                    value: race.race_no,
                    label: race.label || `${race.race_tag} – ${race.track_name}`,
                }))}
            />
        </label>
    );
}

function DriverSelectInline({
                                value,
                                onChange,
                                drivers,
                                disabled,
                                isAdmin,
                                ownSeasonDriver,
                            }) {
    if (!isAdmin && ownSeasonDriver) {
        return (
            <div
                className="rv-dd"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                }}
            >
                <span className="rv-dd__label">Fahrer</span>
                <strong>{formatDriverLabel(ownSeasonDriver)}</strong>
            </div>
        );
    }

    return (
        <label
            className="rv-dd"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
            }}
        >
            <span className="rv-dd__label">Fahrer</span>

            <CustomSelect
                value={value ?? ""}
                onChange={(nextValue) => onChange?.(nextValue)}
                disabled={disabled}
                placeholder={disabled ? "Lade Fahrer…" : "Fahrer auswählen…"}
                options={drivers.map((driver) => ({
                    value: driver.season_driver_id,
                    label: formatDriverLabel(driver),
                }))}
            />
        </label>
    );
}

function AbmeldungenTable({ entries, onWithdraw, busy }) {
    return (
        <div className="card overview-card">
            <div className="fancyTable startfreigabe-table-wrap">
                <table className="tbl tbl-start">
                    <thead>
                    <tr>
                        <th>Position</th>
                        <th>Fahrer</th>
                        <th>Timestamp</th>
                        <th>Aktion</th>
                    </tr>
                    </thead>

                    <tbody>
                    {entries.length ? (
                        entries.map((entry) => (
                            <tr key={entry.absence_id}>
                                <td>P{entry.grid_position}</td>
                                <td>{entry.driver_name}</td>
                                <td>{formatDateTime(entry.cancelled_at)}</td>
                                <td>
                                    {entry.can_withdraw ? (
                                        <button
                                            type="button"
                                            className="btn"
                                            disabled={busy}
                                            onClick={() => onWithdraw(entry.absence_id)}
                                        >
                                            Abmeldung zurückziehen
                                        </button>
                                    ) : (
                                        <span className="text-dim">
                                            Keine Berechtigung
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="align-left text-dim">
                                Für dieses Rennen hat sich aktuell kein Fahrer abgemeldet.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function WaitlistTable({ drivers }) {
    return (
        <div className="card overview-card">
            <h2 style={{ marginTop: 0 }}>
                Startberechtigte Fahrer von der Warteliste
            </h2>

            <p className="muted" style={{ marginTop: -4 }}>
                Anzahl aktiver Abmeldungen regulärer Fahrer = Anzahl freier Wartelistenplätze.
            </p>

            <div className="fancyTable startfreigabe-table-wrap">
                <table className="tbl tbl-start">
                    <thead>
                    <tr>
                        <th>Wartelistenplatz</th>
                        <th>Fahrer</th>
                        <th>Team</th>
                        <th>Auto/Klasse</th>
                    </tr>
                    </thead>

                    <tbody>
                    {drivers.length ? (
                        drivers.map((driver) => (
                            <tr key={driver.season_driver_id}>
                                <td>P{driver.grid_position}</td>
                                <td>{driver.driver_name}</td>
                                <td>{driver.team_name || "–"}</td>
                                <td>
                                    {driver.car_number ? `#${driver.car_number}` : "–"}
                                    {driver.class_name ? ` / ${driver.class_name}` : ""}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="align-left text-dim">
                                Aktuell ist kein Fahrer von der Warteliste startberechtigt.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CurrentUserBox({ state }) {
    const user = state?.current_user;
    const own = state?.own_season_driver;

    if (!user) return null;

    return (
        <div
            className="card overview-card"
            style={{
                display: "grid",
                gap: 6,
            }}
        >
            <div>
                <b>Login:</b>{" "}
                {user.display_name || user.discord_id || "Discord User"}
                {" "}
                <span className="text-dim">
                    ({user.is_admin ? "Admin" : "User"})
                </span>
            </div>

            {own ? (
                <div>
                    <b>Verknüpfter Fahrer:</b>{" "}{formatDriverLabel(own)}
                </div>
            ) : (
                <div style={{ color: "#ff8c8c" }}>
                    Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet.
                </div>
            )}
        </div>
    );
}

export default function Rennabmeldung() {
    const [races, setRaces] = useState([]);
    const [autoRace, setAutoRace] = useState(null);

    const [submitRace, setSubmitRace] = useState("");
    const [viewRace, setViewRace] = useState("");

    const [drivers, setDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");

    const [state, setState] = useState(null);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const isAdmin = Boolean(state?.current_user?.is_admin);
    const ownSeasonDriver = state?.own_season_driver || null;
    const needsDriverLink = Boolean(state?.needs_driver_link);

    const selectedSubmitRace = useMemo(() => {
        return races.find((race) => Number(race.race_no) === Number(submitRace)) || null;
    }, [races, submitRace]);

    const selectedViewRace = useMemo(() => {
        return races.find((race) => Number(race.race_no) === Number(viewRace)) || null;
    }, [races, viewRace]);

    const cancelledDrivers = useMemo(() => {
        return Array.isArray(state?.cancelled_drivers)
            ? state.cancelled_drivers
            : [];
    }, [state]);

    const eligibleWaitlistDrivers = useMemo(() => {
        return Array.isArray(state?.eligible_waitlist_drivers)
            ? state.eligible_waitlist_drivers
            : [];
    }, [state]);

    const loadState = useCallback(async (raceNo) => {
        if (!raceNo) return null;

        const data = await apiFetch(
            `/state?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}&race_no=${encodeURIComponent(raceNo)}`
        );

        setState(data);
        return data;
    }, []);

    const loadDrivers = useCallback(async () => {
        const data = await apiFetch(
            `/drivers?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}&regular_only=true`
        );

        const list = Array.isArray(data?.drivers) ? data.drivers : [];
        setDrivers(list);

        if (list.length === 1) {
            setSelectedDriverId(String(list[0].season_driver_id));
        }

        return list;
    }, []);

    const reloadAll = useCallback(async () => {
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            const racesData = await apiFetch(
                `/races?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}`
            );

            const raceList = Array.isArray(racesData?.races) ? racesData.races : [];
            const auto = racesData?.auto_race || raceList[0] || null;
            const autoNo = auto?.race_no || "";

            setRaces(raceList);
            setAutoRace(auto);

            setSubmitRace((prev) => prev || autoNo);
            setViewRace((prev) => prev || autoNo);

            await loadDrivers();

            if (autoNo) {
                await loadState(autoNo);
            }
        } catch (e) {
            console.error(e);

            if (e.message === "LOGIN_REQUIRED") {
                setErrorMsg("Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.");
            } else {
                setErrorMsg(e.message || "Fehler beim Laden der Rennabmeldung.");
            }
        } finally {
            setLoading(false);
        }
    }, [loadDrivers, loadState]);

    useEffect(() => {
        reloadAll();
    }, [reloadAll]);

    useEffect(() => {
        if (!viewRace) return;

        let cancelled = false;

        (async () => {
            try {
                if (!cancelled) {
                    await loadState(viewRace);
                }
            } catch (e) {
                console.error(e);

                if (!cancelled) {
                    if (e.message === "LOGIN_REQUIRED") {
                        setErrorMsg("Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.");
                    } else {
                        setErrorMsg(e.message || "Fehler beim Laden des Rennstatus.");
                    }
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [viewRace, loadState]);

    useEffect(() => {
        if (!selectedDriverId && ownSeasonDriver?.season_driver_id) {
            setSelectedDriverId(String(ownSeasonDriver.season_driver_id));
        }
    }, [ownSeasonDriver, selectedDriverId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrorMsg("");
        setSuccessMsg("");

        if (!submitRace) {
            setErrorMsg("Bitte ein Rennen auswählen.");
            return;
        }

        if (needsDriverLink) {
            setErrorMsg(
                "Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet. Bitte melde dich bei der Ligaleitung."
            );
            return;
        }

        if (isAdmin && !selectedDriverId) {
            setErrorMsg("Bitte als Admin einen Fahrer auswählen.");
            return;
        }

        setBusy(true);

        try {
            const body = {
                series_key: SERIES_KEY,
                season_key: SEASON_KEY,
                race_no: Number(submitRace),
            };

            if (selectedDriverId) {
                body.season_driver_id = Number(selectedDriverId);
            }

            const data = await apiFetch("/cancel", {
                method: "POST",
                body: JSON.stringify(body),
            });

            setState(data);
            setViewRace(Number(submitRace));

            const raceLabel =
                selectedSubmitRace?.label ||
                `R${submitRace}`;

            setSuccessMsg(`Abmeldung für ${raceLabel} wurde gespeichert.`);

            if (isAdmin) {
                setSelectedDriverId("");
            }
        } catch (e2) {
            console.error(e2);
            setErrorMsg(e2.message || "Fehler beim Speichern der Abmeldung.");
        } finally {
            setBusy(false);
        }
    };

    const handleWithdraw = async (absenceId) => {
        setErrorMsg("");
        setSuccessMsg("");

        if (!viewRace || !absenceId) return;

        setBusy(true);

        try {
            const data = await apiFetch("/withdraw", {
                method: "POST",
                body: JSON.stringify({
                    series_key: SERIES_KEY,
                    season_key: SEASON_KEY,
                    race_no: Number(viewRace),
                    absence_id: Number(absenceId),
                }),
            });

            setState(data);

            const raceLabel =
                selectedViewRace?.label ||
                `R${viewRace}`;

            setSuccessMsg(`Die Abmeldung für ${raceLabel} wurde zurückgezogen.`);
        } catch (e2) {
            console.error(e2);
            setErrorMsg(e2.message || "Fehler beim Zurückziehen der Abmeldung.");
        } finally {
            setBusy(false);
        }
    };

    const showLoginBox =
        errorMsg === "Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.";

    return (
        <>
            <div className="card overview-card-top">
                <h1>Rennabmeldung – GT3 Xperience</h1>

                <p className="muted">
                    Hier können Stammfahrer ihre Teilnahme für ein einzelnes Rennen abmelden.
                    Die Daten kommen direkt aus der RaceVision-Datenbank.
                    Fahrer von der Warteliste werden automatisch anhand der aktiven Abmeldungen berechnet.
                </p>

                {autoRace ? (
                    <p className="muted" style={{ marginBottom: 0 }}>
                        Automatisch vorausgewähltes Rennen:{" "}
                        <b>
                            {autoRace.label ||
                                `${autoRace.race_tag} – ${autoRace.track_name}`}
                        </b>
                        {" "}
                        <span className="text-dim">
                            ({formatDateTime(autoRace.briefing_from)} bis {formatDateTime(autoRace.results_from)})
                        </span>
                    </p>
                ) : null}
            </div>

            {showLoginBox ? (
                <div className="card overview-card">
                    <StatusMessage type="error">{errorMsg}</StatusMessage>

                    <a
                        className="btn btn-primary"
                        href={AUTH_LOGIN_URL}
                        style={{ marginTop: 12, display: "inline-flex" }}
                    >
                        Mit Discord anmelden
                    </a>
                </div>
            ) : null}

            {!showLoginBox ? <CurrentUserBox state={state} /> : null}

            <div className="card overview-card">
                <h2 style={{ marginTop: 0 }}>Fahrer abmelden</h2>

                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <RaceSelect
                        label="Rennen"
                        value={submitRace}
                        onChange={setSubmitRace}
                        races={races}
                        disabled={busy || loading || !races.length}
                    />

                    <DriverSelectInline
                        value={selectedDriverId}
                        onChange={setSelectedDriverId}
                        drivers={drivers}
                        disabled={busy || loading || !drivers.length}
                        isAdmin={isAdmin}
                        ownSeasonDriver={ownSeasonDriver}
                    />

                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={busy || loading || !submitRace || needsDriverLink || showLoginBox}
                    >
                        {busy ? "Sende…" : "Abmelden"}
                    </button>
                </form>

                {needsDriverLink ? (
                    <StatusMessage type="error">
                        Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet.
                        Bitte melde dich bei der Ligaleitung.
                    </StatusMessage>
                ) : null}

                <StatusMessage type="success">{successMsg}</StatusMessage>

                <StatusMessage type="error">
                    {errorMsg && !showLoginBox ? errorMsg : ""}
                </StatusMessage>
            </div>

            <hr
                style={{
                    margin: "26px 0",
                    borderColor: "rgba(255,255,255,0.12)",
                }}
            />

            <div className="card overview-card" style={{ display: "grid", gap: 12 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <div>
                        <h2 style={{ margin: 0 }}>Abgemeldete Fahrer</h2>
                        <p className="muted" style={{ margin: "6px 0 0" }}>
                            Wähle ein Rennen aus, um die aktuellen Abmeldungen zu sehen.
                            {selectedViewRace ? (
                                <>
                                    {" "}
                                    Aktuell angezeigt:{" "}
                                    <b>
                                        {selectedViewRace.label ||
                                            `${selectedViewRace.race_tag} – ${selectedViewRace.track_name}`}
                                    </b>
                                </>
                            ) : null}
                        </p>
                    </div>

                    <RaceSelect
                        label="Anzeige"
                        value={viewRace}
                        onChange={setViewRace}
                        races={races}
                        disabled={busy || loading || !races.length || showLoginBox}
                    />
                </div>
            </div>

            {loading ? (
                <div className="card start-card-top">
                    <p>Lade Daten…</p>
                </div>
            ) : (
                <>
                    <AbmeldungenTable
                        entries={cancelledDrivers}
                        onWithdraw={handleWithdraw}
                        busy={busy}
                    />

                    <WaitlistTable drivers={eligibleWaitlistDrivers} />
                </>
            )}
        </>
    );
}
